/**
 * Header Component - Premium Design
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, useThemeStore } from '../stores';

export function Header() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuthStore();
    const { theme, toggleTheme } = useThemeStore();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getTitle = () => {
        const path = location.pathname;
        if (path === '/') return 'Dashboard';
        if (path === '/projects') return 'All Projects';
        if (path.startsWith('/collections/')) return 'Content Manager';
        if (path === '/media') return 'Media Library';
        if (path === '/schema') return 'Schema Builder';
        if (path === '/settings') return 'Global Settings';
        return 'TPCMS';
    };

    return (
        <header
            className="h-16 flex items-center justify-between px-8 z-30 sticky top-0"
            style={{
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-primary)'
            }}
        >
            {/* Context Title */}
            <div>
                <h1
                    className="text-xl font-bold tracking-tight cursor-pointer hover:opacity-80 transition-all active:scale-95"
                    style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}
                    onClick={() => navigate('/')}
                >
                    {getTitle()}
                </h1>
            </div>

            {/* Actions Area */}
            <div className="flex items-center gap-4">
                {/* Search Bar */}
                <div className="hidden md:flex relative group">
                    <input
                        type="text"
                        placeholder="Quick search (Ctrl+K)"
                        className="input w-80 pl-10 pr-4 py-2 text-xs font-medium"
                        style={{
                            backgroundColor: 'var(--bg-tertiary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px'
                        }}
                    />
                    <span
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm opacity-50 group-focus-within:opacity-100 transition-opacity"
                    >
                        🔍
                    </span>
                </div>

                <div className="h-6 w-px mx-1" style={{ backgroundColor: 'var(--border-color)' }}></div>

                {/* Theme Switcher */}
                <button
                    onClick={toggleTheme}
                    className="w-10 h-10 flex items-center justify-center rounded-xl transition-all hover:scale-105 active:scale-95"
                    style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                    title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                    <span className="text-lg leading-none">
                        {theme === 'dark' ? '☀️' : '🌙'}
                    </span>
                </button>

                {/* User Dropdown */}
                <div className="relative group">
                    <button
                        className="flex items-center gap-3 pl-1 pr-3 py-1 rounded-2xl transition-all hover:bg-opacity-10 group-hover:bg-blue-500/5 group"
                    >
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-blue-500/25 transition-transform group-hover:scale-105">
                            {user?.name?.charAt(0) || 'A'}
                        </div>
                        <div className="text-left hidden lg:block">
                            <div className="text-xs font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
                                {user?.name || 'Administrator'}
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-tighter opacity-50" style={{ color: 'var(--text-muted)' }}>
                                Developer
                            </div>
                        </div>
                        <span className="text-[10px] opacity-40 ml-1">▼</span>
                    </button>

                    {/* Dropdown Menu */}
                    <div
                        className="absolute right-0 top-full mt-2 w-56 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 origin-top-right transform scale-95 group-hover:scale-100 z-50 p-2 overflow-hidden"
                        style={{
                            backgroundColor: 'var(--bg-primary)',
                            border: '1px solid var(--border-color)',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                    >
                        <div className="px-4 py-3 mb-2">
                            <div className="text-xs font-bold uppercase tracking-widest leading-none mb-1" style={{ color: 'var(--text-muted)' }}>
                                Signed in as
                            </div>
                            <div className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>
                                {user?.email || 'admin@tpcms.dev'}
                            </div>
                        </div>

                        <div className="space-y-1">
                            <button
                                onClick={() => navigate('/settings')}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-semibold rounded-xl hover:bg-blue-500/10 hover:text-blue-500 transition-colors"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                <span>👤</span> Profile Settings
                            </button>
                            <button
                                onClick={() => navigate('/settings')}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-semibold rounded-xl hover:bg-blue-500/10 hover:text-blue-500 transition-colors"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                <span>🔑</span> Access Keys
                            </button>

                            <hr className="my-2" style={{ borderColor: 'var(--border-color)' }} />

                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm font-bold rounded-xl hover:bg-red-500/10 text-red-500 transition-colors"
                            >
                                <span>🚪</span> Sign out
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
